import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PhoneCall, MessageSquare, Phone, Calendar, Clock, Building2,
  ExternalLink, MapPin, ChevronLeft, ChevronRight, Check,
  LayoutList, Grid, Search, ShieldCheck, Mail, CalendarDays
} from "lucide-react";
import { api, formatApiError, prefixUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";

const TIME_SLOTS = ["10:00 AM", "11:30 AM", "1:00 PM", "3:00 PM", "5:00 PM"];

function slotTo24h(slot) {
  if (!slot) return "10:00";
  const [time, mod] = slot.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (mod === "PM" && h !== 12) h += 12;
  if (mod === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
}

export default function Enquiries() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Booking appointment states
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    date: "",
    time_slot: "10:00 AM",
    note: ""
  });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setBookingForm(prev => ({
        ...prev,
        buyer_name: user.name || "",
        buyer_email: user.email || "",
        buyer_phone: user.phone || "",
        date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
      }));
    }
  }, [user, bookingOpen]);

  const getImageUrl = (urlObj) => {
    if (!urlObj) return "";
    const url = typeof urlObj === "string" ? urlObj : (urlObj.url || urlObj.file_url || urlObj.public_url || "");
    if (!url) return "";
    return prefixUrl(url);
  };

  const load = () => {
    setLoading(true);
    setError("");
    api.get("/my/enquiries")
      .then(({ data }) => {
        setItems(data.items || []);
      })
      .catch((e) => setError(formatApiError(e) || "Enquiries could not be loaded. Please try again."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Filter counts
  const propertiesCount = items.filter(it => it.property_id && it.source !== "PLOT_DEMAND").length;
  const plotsCount = items.filter(it => it.source === "PLOT_DEMAND").length;

  const categories = [
    { id: "all", label: "All Enquiries", count: items.length },
    { id: "properties", label: "Properties", count: propertiesCount },
    { id: "plots", label: "Plots on Demand", count: plotsCount },
  ];

  // Local filtering & search logic
  const filteredItems = items.filter(it => {
    // Tab category filter
    if (activeTab === "properties") {
      if (!it.property_id || it.source === "PLOT_DEMAND") return false;
    } else if (activeTab === "plots") {
      const isPlot = it.source === "PLOT_DEMAND";
      if (!isPlot) return false;
    }

    // Search query filter
    const query = searchQuery.toLowerCase().trim();
    if (query !== "") {
      const matchesTitle = String(it.property_title || "").toLowerCase().includes(query);
      const matchesAgent = String(it.agent_name || "").toLowerCase().includes(query);
      const matchesSource = String(it.source || "").toLowerCase().includes(query);
      if (!matchesTitle && !matchesAgent && !matchesSource) return false;
    }

    return true;
  });

  // Pagination logic
  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  // Reset page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, itemsPerPage]);

  const parseDateTime = (dtStr) => {
    if (!dtStr) return "Date unavailable";
    try {
      const d = new Date(dtStr);
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    } catch {
      return dtStr;
    }
  };

  const getOverlayLabel = (item) => {
    if (item.source === "PLOT_DEMAND") return "PLOTS ON DEMAND";
    return String(item.property_category || "PROPERTY").replaceAll("_", " ");
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please log in to book a site visit");
      return;
    }
    setBookingSubmitting(true);
    try {
      await api.post("/appointments", {
        buyer_name: bookingForm.buyer_name,
        buyer_email: bookingForm.buyer_email,
        buyer_phone: bookingForm.buyer_phone,
        date: bookingForm.date,
        time_slot: bookingForm.time_slot,
        note: bookingForm.note,
        buyer_user_id: user.user_id || user.id,
        property_id: selectedPropertyId,
        visit_at: `${bookingForm.date}T${slotTo24h(bookingForm.time_slot)}:00`
      });
      toast.success("Site visit requested! Agent/office will confirm shortly.");
      setBookingOpen(false);
      setBookingForm(prev => ({ ...prev, note: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally {
      setBookingSubmitting(false);
    }
  };

  return (
    <div data-testid="enquiries-page" className="space-y-6 rounded-[32px] bg-secondary/20 p-4 md:p-6">
      {/* Header section */}
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-primary mb-1">
          My Enquiries & Contacts
        </h1>
        <p className="text-muted-foreground text-sm">
          Track and manage your property interests and agent communications.
        </p>
      </div>

      {/* Dynamic Status Tabs & Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pt-2">
        <div className="w-full lg:w-auto bg-card/80 backdrop-blur border border-border p-1 rounded-2xl flex flex-wrap gap-1 shrink-0">
          {categories.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex-1 lg:flex-none text-center px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-colors duration-200 ${
                  isActive ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeEnquiryTab"
                    className="absolute inset-0 bg-primary rounded-xl shadow-sm z-[-1]"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                <span>{tab.label}</span>
                <span className={`ml-1.5 px-1.5 py-0.2 rounded-full text-[9px] font-black tracking-tight ${
                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search capsule */}
        <div className="relative w-full lg:w-80">
          <Search className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by property, agent or action"
            className="w-full pl-11 pr-4 py-2.5 bg-background border border-border rounded-xl text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors shadow-sm"
          />
        </div>
      </div>

      {/* Main List of Inquiries */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-[28px] p-5 h-44 animate-pulse space-y-4">
              <div className="flex gap-4">
                <div className="w-full md:w-56 h-32 bg-muted rounded-2xl shrink-0" />
                <div className="flex-1 space-y-3">
                  <div className="h-5 bg-muted rounded-lg w-2/3" />
                  <div className="h-4 bg-muted rounded-lg w-1/3" />
                  <div className="h-10 bg-muted rounded-xl w-full mt-4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive/30 bg-destructive/5 p-10 text-center" role="alert">
          <p className="font-bold text-foreground">Enquiries could not be loaded</p>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <Button type="button" onClick={load} className="mt-4">Try again</Button>
        </Card>
      ) : paginatedItems.length === 0 ? (
        <Card className="border border-dashed border-border rounded-3xl py-16 text-center bg-card shadow-sm text-muted-foreground p-6 space-y-4">
          <span className="inline-flex h-16 w-16 mx-auto items-center justify-center rounded-2xl bg-muted border border-border text-muted-foreground animate-float-soft">
            <PhoneCall className="w-8 h-8"/>
          </span>
          <div>
            <p className="font-semibold text-foreground text-base">No enquiries found matching filters.</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">Explore published properties or raise a custom Plots on Demand request to get started.</p>
          </div>
          <div className="flex items-center justify-center gap-3 pt-2">
            <Button onClick={() => navigate("/properties")} className="font-bold text-xs px-5 py-2.5 rounded-xl shadow-sm">
              Explore Properties & Make Enquiry
            </Button>
            <Button variant="outline" onClick={() => navigate("/dashboard/plot-demands")} className="font-bold text-xs px-5 py-2.5 rounded-xl">
              Open Plots on Demand
            </Button>
          </div>
        </Card>
      ) : (
        <motion.div
          key={`${activeTab}-${currentPage}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="space-y-4"
          data-testid="enquiries-list-container"
        >
          {paginatedItems.map((item) => {
            const pid = item.property_id;
            const imgUrl = getImageUrl(item.property_thumbnail_url);
            const contactedDate = parseDateTime(item.created_at);
            const overlayTag = getOverlayLabel(item);

            return (
              <Card
                key={item.id}
                className="bg-card border border-border rounded-[28px] p-5 hover:shadow-md transition duration-200 relative"
                data-testid={`enq-${item.id}`}
              >
                <div className="flex flex-col md:flex-row gap-5">
                  {/* Photo Thumbnail */}
                  <div className="w-full md:w-56 h-36 rounded-2xl overflow-hidden relative shadow-inner bg-muted shrink-0 border border-border">
                    {imgUrl ? (
                      <img src={imgUrl} alt={item.property_title || "Property"} loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">Image unavailable</div>
                    )}
                    {/* Action Tag overlay */}
                    <div className="absolute top-2.5 left-2.5 z-10">
                      <span className="text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded bg-primary text-primary-foreground shadow-sm">
                        {overlayTag}
                      </span>
                    </div>
                  </div>

                  {/* Details Area */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        {pid ? (
                          <Link to={`/properties/${pid}`} className="font-display font-black text-xl text-primary hover:underline transition leading-snug">
                            {item.property_title || "Property details unavailable"}
                          </Link>
                        ) : (
                          <h3 className="font-display font-black text-xl text-primary leading-snug">
                            {item.property_title || "Plots on Demand request"}
                          </h3>
                        )}
                        {/* Status tag */}
                        <div className="shrink-0">
                          <span className="bg-muted border border-border text-muted-foreground rounded-full px-3 py-1 flex items-center gap-1.5 text-[10px] font-extrabold tracking-wide uppercase">
                            {String(item.status || "NEW").replaceAll("_", " ")}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-semibold mt-2.5">
                        <CalendarDays className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span>Contacted on {contactedDate}</span>
                      </div>
                    </div>

                    {/* Divider line & Representative box */}
                    <div className="border-t border-border mt-4 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      {/* Left: Representative */}
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-foreground">
                            {item.agent_name ? String(item.agent_name).slice(0, 2).toUpperCase() : "—"}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <h5 className="font-bold text-foreground text-sm leading-tight">
                            {item.agent_name || "Representative not assigned"}
                          </h5>
                          <p className="text-xs text-muted-foreground truncate">
                           {item.agent_name ? "Assigned representative" : "Assignment pending"}
                          </p>
                        </div>
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        {pid ? (
                          <Button asChild variant="outline" className="text-xs font-bold px-4 py-2 rounded-xl"><Link to={`/properties/${pid}`}>View Details</Link></Button>
                        ) : (
                          <Button variant="outline" onClick={() => navigate("/dashboard/plot-demands")} className="text-xs font-bold px-4 py-2 rounded-xl">
                            View Details
                          </Button>
                        )}

                        {item.agent_phone && (
                          <a
                            href={`tel:${item.agent_phone}`}
                            className="w-9 h-9 rounded-xl border border-border hover:bg-muted text-foreground flex items-center justify-center transition shadow-sm shrink-0"
                            title="Call Representative"
                            aria-label={`Call ${item.agent_name || "assigned representative"}`}
                          >
                            <Phone className="w-4 h-4" />
                          </a>
                        )}

                        {/* Schedule Visit button (Triggers visit schedule form) */}
                        {pid && (
                          <Button
                            onClick={() => {
                              setSelectedPropertyId(pid);
                              setBookingOpen(true);
                            }}
                            className="text-xs font-bold px-5 py-2.5 rounded-xl shadow-sm"
                          >
                            Schedule Visit
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </motion.div>
      )}

      {/* Pagination footer */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-border text-xs text-muted-foreground font-semibold">
          <span>
            Showing {startIndex + 1} to {endIndex} of {totalItems} enquiries
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg bg-card border border-border text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous enquiries page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => {
              const pageNum = i + 1;
              const isActive = currentPage === pageNum;
              return (
                <button
                  type="button"
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-card border border-border text-muted-foreground hover:text-foreground"
                  }`}
                  aria-label={`Go to enquiries page ${pageNum}`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg bg-card border border-border text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next enquiries page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Book a property visit Dialog/Modal matching the mockup exactly */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="sm:max-w-md bg-card rounded-3xl p-6 border border-border shadow-2xl" data-testid="appointment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black text-primary">
              Book a property visit
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBookingSubmit} className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-bold text-foreground block mb-1">Your Name</label>
              <input
                value={bookingForm.buyer_name}
                onChange={(e) => setBookingForm({ ...bookingForm, buyer_name: e.target.value })}
                required
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                data-testid="appt-name"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-foreground block mb-1">Email</label>
              <input
                type="email"
                value={bookingForm.buyer_email}
                onChange={(e) => setBookingForm({ ...bookingForm, buyer_email: e.target.value })}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                data-testid="appt-email"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-foreground block mb-1">Phone</label>
              <input
                value={bookingForm.buyer_phone}
                onChange={(e) => setBookingForm({ ...bookingForm, buyer_phone: e.target.value })}
                required
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                data-testid="appt-phone"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-foreground block mb-1">Date</label>
                <input
                  type="date"
                  value={bookingForm.date}
                  onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value })}
                  required
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                  data-testid="appt-date"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-foreground block mb-1">Time</label>
                <select
                  value={bookingForm.time_slot}
                  onChange={(e) => setBookingForm({ ...bookingForm, time_slot: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                  data-testid="appt-time"
                >
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-foreground block mb-1">Notes</label>
              <textarea
                value={bookingForm.note}
                onChange={(e) => setBookingForm({ ...bookingForm, note: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:border-primary transition-colors"
                data-testid="appt-notes"
              />
            </div>
            <Button
              type="submit"
              disabled={bookingSubmitting}
              className="w-full font-bold py-3 rounded-xl transition duration-200 mt-2 shadow-sm text-xs uppercase tracking-wide"
              data-testid="appt-submit"
            >
              {bookingSubmitting ? "Booking…" : "Confirm Visit Request"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
